/**
 * GFG-273 (Campaigns tab, GFG Admin) — the Campaigns table, presentational only: it renders the
 * columns it is handed, reports header clicks upward and owns no data, no query and no dialog.
 *
 * Deliberately NOT `Blotter`: that component renders a search box and a pager unconditionally,
 * and this tab has neither (its controls bar is its own and it loads a single page). The `th`/`td`
 * styles and the `blotter-row` class below are copied from it so the two tables stay visually
 * identical, and the three fixed columns are frozen to the left edge while the other thirteen
 * scroll.
 */
import { ArrowDownward, ArrowUpward, UnfoldMore } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import { type CSSProperties, useLayoutEffect, useRef, useState } from "react";
import { COPY } from "@/constants/adminCampaigns";
import type {
  AdminCampaignRow,
  AdminCampaignSortKey,
  CampaignColumn,
} from "@/interfaces/adminCampaigns";

/** Announced while the first page is in flight; the spinner itself carries no text. */
const LOADING_LABEL = "Loading campaigns";

// Matches the design's `.history-table th` (Blotter.tsx) — wax header band, 1px tracking.
const TH: CSSProperties = {
  textAlign: "left",
  padding: "12px 16px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "var(--color-rodeo-black)",
  whiteSpace: "nowrap",
  userSelect: "none",
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "var(--color-wax)",
  boxShadow: "inset 0 -1px 0 var(--color-smoke)",
};

// Matches the design's `.history-table td`.
const TD: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

/**
 * A frozen column needs its own opaque ground (the scrolled columns pass underneath) and its own
 * bottom rule: `border-collapse: collapse` hands borders to the table, which does not paint them
 * on a sticky cell.
 */
const PINNED_CELL: CSSProperties = {
  position: "sticky",
  zIndex: 1,
  background: "var(--color-white)",
  boxShadow: "inset 0 -1px 0 var(--color-smoke)",
};

/** The header half of the same freeze — above both the sticky header row and the pinned cells. */
const PINNED_HEADER: CSSProperties = { position: "sticky", zIndex: 3 };

/** The divider between the frozen block and the columns scrolling under it. */
const PINNED_EDGE = "inset -1px 0 0 var(--color-smoke)";

/** A header that sorts is a real button, so the column is reachable by keyboard, not just mouse. */
const SORT_BUTTON: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  font: "inherit",
  letterSpacing: "inherit",
  textTransform: "inherit",
  color: "inherit",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
};

/**
 * Where the frozen columns actually start. The widths in campaignsColumns.tsx are only hints to the
 * automatic table layout, which redistributes them to fit content and container, so an offset summed
 * from those numbers freezes a column where its neighbour does not end.
 */
function usePinnedOffsets(pinnedCount: number) {
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [offsets, setOffsets] = useState<number[]>([]);

  useLayoutEffect(() => {
    const row = headerRowRef.current;
    if (row === null) return;
    const pinned = Array.from(row.cells).slice(0, pinnedCount);

    function measure() {
      let left = 0;
      const next = pinned.map((cell) => {
        const start = left;
        left += cell.getBoundingClientRect().width;
        return start;
      });
      setOffsets((current) =>
        current.length === next.length && current.every((value, index) => value === next[index])
          ? current
          : next,
      );
    }

    // A sticky offset changes no geometry, so re-measuring cannot feed itself; watching the frozen
    // cells catches the viewport, the web font finishing loading and a column being hidden alike.
    const observer = new ResizeObserver(measure);
    for (const cell of pinned) observer.observe(cell);
    measure();
    return () => observer.disconnect();
  }, [pinnedCount]);

  return { headerRowRef, offsets };
}

export interface CampaignsTableProps {
  rows: AdminCampaignRow[];
  /** Fixed columns first, then the visible configurable ones in the user's order. */
  columns: CampaignColumn[];
  isLoading: boolean;
  /** A failed load shows no rows AND no empty-state text — see `renderBody`. */
  isError: boolean;
  sortBy: AdminCampaignSortKey;
  sortDir: "asc" | "desc";
  onSort: (key: AdminCampaignSortKey) => void;
}

export default function CampaignsTable({
  rows,
  columns,
  isLoading,
  isError,
  sortBy,
  sortDir,
  onSort,
}: CampaignsTableProps) {
  const minWidth = columns.reduce((total, column) => total + column.width, 0);
  const pinned = columns.filter((column) => column.pinnedLeft !== undefined);
  const lastPinnedId = pinned.at(-1)?.id;
  const { headerRowRef, offsets } = usePinnedOffsets(pinned.length);

  function headerStyle(column: CampaignColumn, index: number): CSSProperties {
    if (column.pinnedLeft === undefined) return { ...TH, ...column.headerStyle };
    return {
      ...TH,
      ...PINNED_HEADER,
      left: offsets[index] ?? column.pinnedLeft,
      boxShadow:
        column.id === lastPinnedId ? `${TH.boxShadow}, ${PINNED_EDGE}` : String(TH.boxShadow),
      ...column.headerStyle,
    };
  }

  function cellStyle(column: CampaignColumn, index: number): CSSProperties {
    if (column.pinnedLeft === undefined) return { ...TD, ...column.cellStyle };
    return {
      ...TD,
      ...PINNED_CELL,
      left: offsets[index] ?? column.pinnedLeft,
      boxShadow:
        column.id === lastPinnedId
          ? `${PINNED_CELL.boxShadow}, ${PINNED_EDGE}`
          : String(PINNED_CELL.boxShadow),
      ...column.cellStyle,
    };
  }

  function renderHeaderCell(column: CampaignColumn, index: number) {
    const { sortKey } = column;
    const isSorted = sortKey !== null && sortKey === sortBy;
    return (
      <th
        key={column.id}
        scope="col"
        style={{ ...headerStyle(column, index), width: column.width }}
        aria-sort={isSorted ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
      >
        {sortKey === null ? (
          column.label
        ) : (
          <button type="button" style={SORT_BUTTON} onClick={() => onSort(sortKey)}>
            {column.label}
            {/* Distinct glyphs, not one icon rotated — direction must read at a glance. */}
            {isSorted ? (
              sortDir === "asc" ? (
                <ArrowUpward sx={{ fontSize: 14, opacity: 0.9 }} />
              ) : (
                <ArrowDownward sx={{ fontSize: 14, opacity: 0.9 }} />
              )
            ) : (
              <UnfoldMore sx={{ fontSize: 14, opacity: 0.35 }} />
            )}
          </button>
        )}
      </th>
    );
  }

  /**
   * A failed load renders neither rows nor the empty-state sentence: "No campaigns match these
   * filters" would blame the filters for a request that never answered. The panel's banner above
   * the table says what actually happened and offers the retry.
   */
  function renderBody() {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={columns.length} style={{ ...TD, textAlign: "center", padding: 40 }}>
            <div role="status" aria-busy="true" aria-label={LOADING_LABEL}>
              <CircularProgress size={22} aria-hidden />
            </div>
          </td>
        </tr>
      );
    }
    if (isError) return null;
    if (rows.length === 0) {
      return (
        <tr>
          <td
            colSpan={columns.length}
            style={{ ...TD, textAlign: "center", padding: 40, color: "var(--color-graphite)" }}
          >
            {COPY.emptyState}
          </td>
        </tr>
      );
    }
    return rows.map((row) => (
      <tr key={row.id} className="blotter-row">
        {columns.map((column, index) => (
          <td
            key={column.id}
            style={cellStyle(column, index)}
            data-pinned={column.pinnedLeft === undefined ? undefined : true}
          >
            {column.render(row)}
          </td>
        ))}
      </tr>
    ));
  }

  return (
    <Box
      sx={{
        border: "1px solid var(--color-card-border)",
        borderRadius: "var(--radius-card)",
        bgcolor: "background.paper",
        overflow: "auto",
        maxHeight: "calc(100vh - 300px)",
        // Pinned cells carry their own opaque ground, so the row hover has to be repeated on them
        // or the frozen columns stay white while the rest of the row tints.
        "& .blotter-row:hover td[data-pinned]": { backgroundColor: "var(--color-wax)" },
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
        <thead>
          <tr ref={headerRowRef}>{columns.map(renderHeaderCell)}</tr>
        </thead>
        <tbody>{renderBody()}</tbody>
      </table>
    </Box>
  );
}
